import { createElement, useMemo } from 'react';
import { type TreeNode } from 'primereact/treenode';
import { renderOverflowTooltipLabel } from '../../../_utils/components';
import { getLabelText } from '../../../_utils/plain-label.utils';
import {
    type TreeNodeMaps,
    type TreeSelectSelectionMode,
    type TreeSelectValue,
} from '../models/tree-select-checkbox-selection-behavior';
import { type TreeSelectProps } from '../models/tree-select.types';
import { getSelectedNodes } from '../utils/tree-select-checkbox-selection.utils';
import { type TreeSelectSelectionFlags } from '../utils/tree-select-selection-model.utils';

interface UseTreeSelectValuePresentationInput {
    display: TreeSelectProps['display'];
    inputValueTemplate: TreeSelectProps['valueTemplate'];
    nodeMaps: TreeNodeMaps | undefined;
    placeholder: TreeSelectProps['placeholder'];
    selectionFlags: TreeSelectSelectionFlags;
    selectionMode: TreeSelectSelectionMode | undefined;
    value: TreeSelectValue;
}

export interface TreeSelectValuePresentation {
    display: TreeSelectProps['display'];
    selectedNodes: TreeNode[];
    shouldRenderSelectedPills: boolean;
    valueTemplate: TreeSelectProps['valueTemplate'];
}

export function useTreeSelectValuePresentation({
    display,
    inputValueTemplate,
    nodeMaps,
    placeholder,
    selectionFlags,
    selectionMode,
    value,
}: UseTreeSelectValuePresentationInput): TreeSelectValuePresentation {
    const selectedNodes = useMemo(
        () =>
            selectionFlags.useExternalSelectedValueLayout ||
            selectionFlags.shouldUseOverflowValueTemplate
                ? getSelectedNodes(value, selectionMode, nodeMaps)
                : [],
        [
            nodeMaps,
            selectionFlags.shouldUseOverflowValueTemplate,
            selectionFlags.useExternalSelectedValueLayout,
            selectionMode,
            value,
        ],
    );

    const valueTemplate = useMemo(
        () =>
            resolveValueTemplate({
                inputValueTemplate,
                placeholder,
                selectedNodes,
                selectionFlags,
                selectionMode,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            inputValueTemplate,
            placeholder,
            selectedNodes,
            selectionMode,
            selectionFlags.shouldUseOverflowValueTemplate,
            selectionFlags.useExternalSelectedValueLayout,
        ],
    );

    return {
        display: selectionFlags.useExternalSelectedValueLayout
            ? 'comma'
            : display,
        selectedNodes,
        shouldRenderSelectedPills:
            selectionFlags.useExternalSelectedValueLayout &&
            selectedNodes.length > 0,
        valueTemplate,
    };
}

function resolveValueTemplate({
    inputValueTemplate,
    placeholder,
    selectedNodes,
    selectionFlags,
    selectionMode,
}: {
    inputValueTemplate: TreeSelectProps['valueTemplate'];
    placeholder: TreeSelectProps['placeholder'];
    selectedNodes: TreeNode[];
    selectionFlags: TreeSelectSelectionFlags;
    selectionMode: TreeSelectSelectionMode | undefined;
}): TreeSelectProps['valueTemplate'] {
    if (selectionFlags.useExternalSelectedValueLayout) {
        return function externalValueTemplate() {
            return placeholder ?? null;
        };
    }

    if (!selectionFlags.shouldUseOverflowValueTemplate) {
        return inputValueTemplate;
    }

    return function overflowValueTemplate() {
        if (selectedNodes.length === 0) {
            return placeholder
                ? createElement('span', null, placeholder)
                : null;
        }

        const label =
            selectionMode === 'single'
                ? getLabelText(selectedNodes[0]?.label)
                : selectedNodes
                      .map((node) => getLabelText(node.label))
                      .filter(
                          (currentLabel): currentLabel is string =>
                              currentLabel !== null,
                      )
                      .join(', ');

        return renderOverflowTooltipLabel(label, 'ics-overflow-label');
    };
}
